// Use both DOMContentLoaded and shopify:section:load events for better reliability
document.addEventListener('DOMContentLoaded', initializeSectionRotation);
document.addEventListener('shopify:section:load', initializeSectionRotation);

/**
 * Initializes the product rotation section functionality.
 * Fetches product data, sets up event listeners for image switching,
 * image zoom, variant selection, quantity controls, and purchase actions.
 */
function initializeSectionRotation() {
  // Fetch product data injected from Liquid
  const productData = getProductData();
  if (!productData) {
    return; // Stop initialization if data is missing
  }

  const { product, variants } = productData;

  // Setup various interactive components
  setupProductImagePopup(); // Product image gallery popup
  setupSizeChartPopup(); // Size chart popup functionality
  setupVariantSelection(variants);
  setupQuantityControls();
  setupAddToCart(product);
  setupBuyNow(product);
}

/**
 * Retrieves product and variant data from hidden JSON elements in the Liquid file.
 * @returns {{product: object, variants: Array<object>} | null} The product data or null if elements are missing.
 */
function getProductData() {
  const productElement = document.getElementById('product-json');
  const variantsElement = document.getElementById('variants-json');

  if (!productElement || !variantsElement) {
    console.warn('Product data not found. Ensure the section-rotation.liquid file includes the necessary JSON data.');
    return null;
  }

  try {
    const product = JSON.parse(productElement.textContent);
    const variants = JSON.parse(variantsElement.textContent);
    return { product, variants };
  } catch (error) {
    console.error('Error parsing product JSON data:', error);
    return null;
  }
}

/**
 * Sets up event listeners for the product image popup gallery.
 */
function setupProductImagePopup() {
  const popup = document.getElementById('productImagePopup');
  const popupImage = document.getElementById('popupImage');
  const closeBtn = popup ? popup.querySelector('.popup-close') : null;
  const prevBtn = popup ? popup.querySelector('.popup-prev') : null;
  const nextBtn = popup ? popup.querySelector('.popup-next') : null;
  const galleryImages = document.querySelectorAll('.product-gallery-image');

  let currentImageIndex = 0;

  if (!popup || !popupImage || !closeBtn || !prevBtn || !nextBtn || galleryImages.length === 0) {
    // Missing elements for popup functionality
    console.warn('Product image popup elements not found.');
    return;
  }

  // Open popup on image click
  galleryImages.forEach((img, index) => {
    img.addEventListener('click', function () {
      const largeImageSrc = this.dataset.largeImage || this.src;
      popupImage.src = largeImageSrc;
      currentImageIndex = index;
      popup.classList.add('active');
      // Set focus to the close button for accessibility
      closeBtn.focus();
    });
  });

  // Close popup function
  function closePopup() {
    popup.classList.remove('active');
    // Optionally return focus to the image that was clicked
    if (galleryImages[currentImageIndex]) {
      galleryImages[currentImageIndex].focus();
    }
  }

  // Navigate images function
  function navigateImages(direction) {
    currentImageIndex += direction;
    if (currentImageIndex < 0) {
      currentImageIndex = galleryImages.length - 1;
    } else if (currentImageIndex >= galleryImages.length) {
      currentImageIndex = 0;
    }
    popupImage.src = galleryImages[currentImageIndex].dataset.largeImage || galleryImages[currentImageIndex].src;
  }

  // Event listeners for controls
  closeBtn.addEventListener('click', function(e) {
    e.stopPropagation();
    closePopup();
  });
  
  prevBtn.addEventListener('click', () => navigateImages(-1));
  nextBtn.addEventListener('click', () => navigateImages(1));

  // Close popup on clicking outside the image content
  popup.addEventListener('click', function (e) {
    if (e.target === popup) {
      closePopup();
    }
  });

  // Close popup on Escape key
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && popup.classList.contains('active')) {
      closePopup();
    }
  });

  // Optional: Add keyboard navigation for arrow keys
  document.addEventListener('keydown', function (e) {
    if (popup.classList.contains('active')) {
      if (e.key === 'ArrowLeft') {
        navigateImages(-1);
      } else if (e.key === 'ArrowRight') {
        navigateImages(1);
      }
    }
  });
}

/**
 * Sets up event listeners for variant selection options.
 * @param {Array<object>} variants - The product variants data.
 */
function setupVariantSelection(variants) {
  let selectedOptions = {};
  const sizeOptions = document.querySelectorAll('.size-option');
  const variantIdInput = document.getElementById('variant-id');

  if (sizeOptions.length === 0 || !variantIdInput) {
    // No variant options or input field to set up
    console.warn('Size options or variant input not found.');
    return;
  }

  // Initialize selectedOptions based on the default variant from Liquid
  const defaultVariantId = parseInt(variantIdInput.value, 10);
  const defaultVariant = variants.find(variant => variant.id === defaultVariantId);

  if (defaultVariant) {
    defaultVariant.options.forEach((optionValue, index) => {
      selectedOptions[index] = optionValue;
    });
    // Also update availability and price for the default variant
    updateAvailability(defaultVariant.available);
    updatePrice(defaultVariant.price, defaultVariant.compare_at_price);
  }

  // Add click event listeners to size options
  sizeOptions.forEach(option => {
    option.addEventListener('click', function () {
      const optionElement = this.closest('[data-option-index]');
      if (!optionElement) {
        console.warn('Option element with data-option-index not found.');
        return;
      }

      const optionIndex = parseInt(optionElement.dataset.optionIndex, 10);
      const value = this.dataset.value;

      // Remove selected class from siblings within the same option group
      optionElement.querySelectorAll('.size-option').forEach(o => {
        o.classList.remove('selected');
        o.setAttribute('aria-selected', 'false');
      });

      // Add selected class and update aria-selected for clicked option
      this.classList.add('selected');
      this.setAttribute('aria-selected', 'true');

      // Store selected option value by its index
      selectedOptions[optionIndex] = value;

      // Find the variant that matches the currently selected options
      const matchingVariant = variants.find(variant => {
        // Check if all selected options match the variant's options
        return variant.options.every((optionValue, index) => {
          // If an option at this index has been selected, check if it matches the variant's value
          return selectedOptions[index] === undefined || selectedOptions[index] === optionValue;
        });
      });

      // Update the hidden variant ID input and UI based on the matching variant
      if (matchingVariant) {
        variantIdInput.value = matchingVariant.id;
        updateAvailability(matchingVariant.available);
        updatePrice(matchingVariant.price, matchingVariant.compare_at_price);
        console.log('Selected variant:', matchingVariant);
      } else {
        // Handle case where no variant matches the selection
        console.warn('No variant found for selected options:', selectedOptions);
        updateAvailability(false);
        updatePrice(null, null);
      }
    });
  });
}

/**
 * Sets up event listeners for quantity increase and decrease buttons.
 */
function setupQuantityControls() {
  const decreaseButton = document.getElementById('decreaseQty');
  const increaseButton = document.getElementById('increaseQty');
  const qtyDisplay = document.getElementById('quantity');
  const qtyInput = document.getElementById('quantity-input');
  let quantity = parseInt(qtyInput.value, 10) || 1; // Initialize from input value

  if (!decreaseButton || !increaseButton || !qtyDisplay || !qtyInput) {
    // Missing elements for quantity controls
    return;
  }

  // Decrease quantity button
  decreaseButton.addEventListener('click', () => {
    if (quantity > 1) {
      quantity--;
      qtyDisplay.textContent = quantity;
      qtyInput.value = quantity;
    }
  });

  // Increase quantity button
  increaseButton.addEventListener('click', () => {
    // Assuming a max quantity of 50 as in the original code
    if (quantity < 50) {
      quantity++;
      qtyDisplay.textContent = quantity;
      qtyInput.value = quantity;
    }
  });
}

/**
 * Sets up the event listener for the "Add to Cart" button.
 * @param {object} product - The product data.
 */
function setupAddToCart(product) {
  const addToCartButton = document.getElementById('addToCart');
  const productForm = document.getElementById('product-form');

  if (!addToCartButton || !productForm) {
    // Missing elements for add to cart
    return;
  }

  addToCartButton.addEventListener('click', function (e) {
    e.preventDefault(); // Prevent default link behavior

    // Check if the button is disabled using aria-disabled
    if (this.getAttribute('aria-disabled') === 'true') {
      console.log('Add to Cart button is disabled.');
      return;
    }

    const formData = new FormData(productForm);

    // Debugging: Log formData contents
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ' + pair[1]);
    }

    // Use Shopify's AJAX API to add to cart
    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json' // Request JSON response
      }
    })
      .then(response => {
        if (!response.ok) {
          // Handle HTTP errors
          return response.json().then(err => { throw new Error(err.description || 'Failed to add to cart'); });
        }
        return response.json();
      })
      .then(data => {
        console.log('Item added to cart:', data);
        // Dispatch a custom event to notify other parts of the application that the cart has been updated
        const addedQuantity = parseInt(formData.get('quantity'), 10) || 1;
        document.dispatchEvent(new CustomEvent('cart:item_added', {
          detail: {
            addedQuantity: addedQuantity
          }
        }));
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        // Provide user feedback on error

      });
  });
}

/**
 * Sets up the event listener for the "Buy Now" button.
 * @param {object} product - The product data.
 */
function setupBuyNow(product) {
  const buyNowButton = document.getElementById('buyNow');
  const productForm = document.getElementById('product-form');

  if (!buyNowButton || !productForm) {
    // Missing elements for buy now
    return;
  }

  buyNowButton.addEventListener('click', function (e) {
    e.preventDefault(); // Prevent default link behavior

    // Check if the button is disabled using aria-disabled
    if (this.getAttribute('aria-disabled') === 'true') {
      console.log('Buy Now button is disabled.');
      return;
    }

    const formData = new FormData(productForm);

    // Add to cart using Shopify's AJAX API, then redirect to checkout
    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json' // Request JSON response
      }
    })
      .then(response => {
        if (!response.ok) {
          // Handle HTTP errors
          return response.json().then(err => { throw new Error(err.description || 'Failed to add to cart for Buy Now'); });
        }
        return response.json();
      })
      .then(data => {
        console.log('Item added to cart, redirecting to checkout:', data);
        // Redirect to the checkout page
        window.location.href = '/checkout';
      })
      .catch(error => {
        console.error('Error during Buy Now process:', error);
        // Provide user feedback on error
        alert(`There was an error processing your request: ${error.message}`);
      });
  });
}

/**
 * Updates the availability status of the purchase buttons.
 * @param {boolean} available - Whether the selected variant is available.
 */
function updateAvailability(available) {
  const addToCartLink = document.getElementById('addToCart');
  const buyNowLink = document.getElementById('buyNow');

  if (!addToCartLink || !buyNowLink) {
    // Missing purchase links
    return;
  }

  if (available) {
    // Enable buttons and restore text
    addToCartLink.removeAttribute('aria-disabled');
    // Restore original text or use a setting if available
    addToCartLink.textContent = addToCartLink.dataset.originalText || 'ADD TO CART';
    buyNowLink.removeAttribute('aria-disabled');
    // Restore original text or use a setting if available
    buyNowLink.textContent = buyNowLink.dataset.originalText || 'BUY NOW';
  } else {
    // Disable buttons and update text to 'Sold Out'
    addToCartLink.setAttribute('aria-disabled', 'true');
    // Store original text before changing
    if (!addToCartLink.dataset.originalText) {
      addToCartLink.dataset.originalText = addToCartLink.textContent;
    }
    addToCartLink.textContent = 'SOLD OUT';

    buyNowLink.setAttribute('aria-disabled', 'true');
    // Store original text before changing
    if (!buyNowLink.dataset.originalText) {
      buyNowLink.dataset.originalText = buyNowLink.textContent;
    }
    // Optionally change Buy Now text to Sold Out or similar
    buyNowLink.textContent = 'SOLD OUT';
  }
}

/**
 * Updates the displayed product price based on the selected variant.
 * @param {number | null} price - The current price of the selected variant (in cents).
 * @param {number | null} comparePrice - The compare at price of the selected variant (in cents).
 */
function updatePrice(price, comparePrice) {
  const currentPriceEl = document.querySelector('.price-display');
  const comparePriceEl = document.querySelector('.price-display-usd');

  if (currentPriceEl) {
    if (price !== null) {
      // Format price from cents to currency string (EGP)
      currentPriceEl.textContent = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'EGP',
        maximumFractionDigits: 0, // Ensure no decimals
        minimumFractionDigits: 0
      }).format(price / 100);
    } else {
      // Restore original text or default
      currentPriceEl.textContent = currentPriceEl.dataset.originalText || 'EGP 500';
    }
  }

  if (comparePriceEl) {
    if (comparePrice !== null && comparePrice > (price || 0)) {
      // Format compare price from cents to currency string (EGP if it is a compare price?)
      // Assuming compare price is also in local currency (EGP) matching the main price
      comparePriceEl.textContent = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'EGP'
      }).format(comparePrice / 100);
      comparePriceEl.style.display = 'inline'; // Show compare price
      comparePriceEl.style.textDecoration = 'line-through'; // Add strikethrough for compare price
    } else {
      // If no compare price, show the secondary USD price (static text)
      comparePriceEl.textContent = comparePriceEl.dataset.originalText || 'USD 10';
      comparePriceEl.style.display = 'inline';
      comparePriceEl.style.textDecoration = 'none';
    }
  }
}

/**
 * Sets up event listeners for the size chart popup.
 */
function setupSizeChartPopup() {
  const sizeChartLink = document.getElementById('size-chart-link');
  const sizeChartPopup = document.getElementById('sizeChartPopup');
  const closeBtn = sizeChartPopup ? sizeChartPopup.querySelector('.popup-close') : null;

  if (!sizeChartLink || !sizeChartPopup || !closeBtn) {
    // Check which one is missing for debugging
    if (!sizeChartLink) console.warn('Size chart link element (#size-chart-link) not found.');
    if (!sizeChartPopup) console.warn('Size chart popup element (#sizeChartPopup) not found.');
    if (sizeChartPopup && !closeBtn) console.warn('Size chart close button (.popup-close) not found.');
    return;
  }

  // Open size chart popup
  sizeChartLink.addEventListener('click', function (e) {
    e.preventDefault();
    sizeChartPopup.classList.add('active');
    // accessibility focus
    if (closeBtn) closeBtn.focus();
    document.body.style.overflow = 'hidden'; // Prevent scrolling
  });

  // Close popup function
  function closeSizeChartPopup() {
    sizeChartPopup.classList.remove('active');
    document.body.style.overflow = '';
    sizeChartLink.focus();
  }

  // Close popup on close button click
  closeBtn.addEventListener('click', closeSizeChartPopup);

  // Close popup on clicking outside the content
  sizeChartPopup.addEventListener('click', function (e) {
    if (e.target === sizeChartPopup) {
      closeSizeChartPopup();
    }
  });

  // Close popup on Escape key
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && sizeChartPopup.classList.contains('active')) {
      closeSizeChartPopup();
    }
  });
}